import { createAsyncThunk } from '@reduxjs/toolkit';
import { CONSTANTS } from '../../../Helpers/Configuration';
import { filterCurriculumValues } from '../../../Helpers/CurriculumHelpers';
import { setValue } from '@nsr/react-form/dist/components/redux/slices/formSlice';
import { setCode, setCurriculumRules, setCurriculumValues, setCurriculumLoading } from '../slices/applicationSlice';
import get from 'lodash/get';
import { setSettingsValue } from '../slices/settingsSlice';
import { api } from '../../../ApiContext/ApiContext';
import { batch } from 'react-redux';
import { navigateQuestions } from '../../../Helpers/ApplicationHelpers';
import { saveQueue } from './saveQueue';

const { curriculumCachedQuestions } = CONSTANTS;
const childQuestions = ['major', 'minor', 'concentration'];

const getTemplate = (state) => {
    let template = state.applicationTemplate;

    if (!template?.id) {
        template = state.template;
    }

    return template;
};

// Exported for testing only
export const _autosaveState = { getQueue: () => saveQueue };

export const autosaveAction = createAsyncThunk(
    'application/autosave',
    async (arg, thunkApi) => {
        const { dispatch } = thunkApi;
        const { application, settings } = thunkApi.getState();

        // Ensure queue has latest revision from Redux on first use
        if (saveQueue.getRevision() == null) {
            saveQueue.setRevision(settings?.revision);
        }

        dispatch(setSettingsValue({
            name: 'autosave.status',
            value: CONSTANTS.autosaveStatuses.SAVING,
        }));

        saveQueue.enqueue({
            type: 'autosave',
            execute: async (revision) => {
                const newApplication = Object.assign({}, application, {
                    values: arg.values,
                    revision,
                });
                // Route through PUT /api/applications with scope=values
                // to get explicit revision conflict detection (409)
                const result = await api.applicationForms.update(newApplication, { scope: 'values' });
                return result;
            },
            onSuccess: (result) => {
                batch(() => {
                    dispatch(setSettingsValue({
                        name: 'autosave.status',
                        value: CONSTANTS.autosaveStatuses.SAVED,
                    }));
                    dispatch(setSettingsValue({
                        name: 'revision',
                        value: result.revision,
                    }));
                });
            },
            onError: () => {
                dispatch(setSettingsValue({
                    name: 'autosave.status',
                    value: CONSTANTS.autosaveStatuses.ERROR,
                }));
            },
        });
    }
);

export const fetchCurriculum = createAsyncThunk(
    'application/fetchCurriculum',
    async (arg, thunkApi) => {
        const state = thunkApi.getState();
        const template = arg.template || getTemplate(state);
        
        if (template?.settings?.curriculumFilteringEnabled) {
            const application = arg.application || state.application;
            const { changedQuestion, changedValue, api } = arg;
            const { dispatch } = thunkApi;
            dispatch(setCurriculumLoading(true));
            try {
                const selectedValues = {
                    [changedQuestion]: changedValue
                };
                const isMajorDescription = changedQuestion === 'majorDescription';
                let curriculumRules;
                let curriculumValues;
                let apiError = null;

                if (isMajorDescription) {
                    selectedValues.major = changedValue.major;
                    selectedValues.program = changedValue.program;
                } else if (changedQuestion && changedValue) {
                    selectedValues[changedQuestion] = changedValue;
                }

                template?.cachedQuestions?.forEach(q => {
                    if (q.key !== changedQuestion && curriculumCachedQuestions.some(
                        cq => cq.key === q.key
                    )) {
                        const value = get(state.application.codes, q.questionPath);

                        if (value) {
                            selectedValues[q.key] = value;
                        }
                    }
                });

                // If the term didn't change and we aren't doing an initial fetch,
                // No reason to refetch rules.
                if (!changedQuestion || changedQuestion === 'term' || curriculumRules) {
                    try {
                        curriculumRules = await api.applicationForms.getCurriculumRules(selectedValues.term);
                    } catch (err) {
                        apiError = err;
                        curriculumRules = null;
                    }
                } else {
                    curriculumRules = application?.curriculumRules || [];
                }

                // If there is an API error, curriculumValues will be set to invalid and the error will be passed along so that the user can be informed, but we still want to attempt to filter values based on whatever rules we do have (even if they may be stale) so that we don't clear values without attempting to validate against any rules. If there is no API error, then curriculumValues will be valid or invalid based on the current rules.
                if (curriculumRules?.length > 0 && !apiError) {
                    curriculumValues = filterCurriculumValues(selectedValues, curriculumRules);
                }
                // Check if a parent question was cleared
                // If so, clear child questions even if curriculumValues is valid
                let isClearing = false;
                if (changedQuestion) {
                    isClearing = changedValue === null || changedValue === undefined || changedValue === '' || (isMajorDescription && !changedValue?.program && !changedValue?.major);
                }
                // if there is no api error and the curriculum values are not valid, or if we are clearing a value, then clear dependent questions and values
                // if there is api error, keep values but set valid to false so that user can still submit but will see a snackbar message about the error but the clearing of dependent questions will not be triggered because the rules were not fetched successfully and we don't want to clear values without the correct rules
                if (!curriculumValues.valid || isClearing) {
                    const childQuestionChanged = childQuestions.includes(changedQuestion);

                    curriculumCachedQuestions.forEach(q => {
                        const templateQuestion = template?.cachedQuestions?.find(
                            tq => tq.key === q.key
                        );
                        const isChangedQuestion = q.key === changedQuestion || (
                            isMajorDescription && (
                                q.key === 'program' || q.key === 'major'
                            )
                        );
                        // When clearing a child question, don't clear its parents
                        const isParentOfChangedQuestion = isClearing && (
                            (changedQuestion === 'program' && (q.key === 'location' || q.key === 'level')) ||
                            (changedQuestion === 'major' && (q.key === 'program' || q.key === 'location' || q.key === 'level')) ||
                            (changedQuestion === 'minor' && (q.key === 'program' || q.key === 'location' || q.key === 'major' || q.key === 'level')) ||
                            (changedQuestion === 'concentration' && (q.key === 'program' || q.key === 'location' || q.key === 'major' || q.key === 'minor' || q.key === 'level'))
                        );

                        // Clear everything but the term, level, and the question that just changed
                        // If a child question changed, don't clear the program.
                        // When clearing, don't clear parent questions.
                        if (
                            templateQuestion && q.key !== 'term' &&
                            q.key !== 'level' && !isChangedQuestion &&
                            (!childQuestionChanged || q.key !== 'program') &&
                            !isParentOfChangedQuestion &&
                            // When clearing a minor, do not clear concentration
                            !(changedQuestion === 'minor' && q.key === 'concentration')
                        ) {
                            dispatch(setValue({
                                name: templateQuestion.questionPath,
                                value: null
                            }));
                            dispatch(setCode({
                                path: templateQuestion.questionPath,
                                code: null
                            }));
                            delete selectedValues[q.key];
                        }
                    });

                    // Also clear the majorDescription field if it's not the question being changed
                    if (!isMajorDescription) {
                        navigateQuestions(template, (p, s, _g, q) => {
                            if (q.id === CONSTANTS.majorDescriptionSelfServiceQuestionId) {
                                const questionPath = s.showRepetition
                                    ? `${p.id}.${s.id}.0.${q.id}`
                                    : `${p.id}.${q.id}`;
                                dispatch(setValue({ name: questionPath, value: null }));
                            }
                        });
                    }

                    // If API errored, keep curriculumValues as invalid
                    if (!curriculumRules) {
                        curriculumValues = { valid: false, values: {}, error: apiError };
                    } else {
                        curriculumValues = filterCurriculumValues(selectedValues, curriculumRules);
                    }
                }

                dispatch(setCurriculumRules(curriculumRules));
                dispatch(setCurriculumValues(curriculumValues));
            } finally {
                dispatch(setCurriculumLoading(false));
            }
        }
    }
);
